import { gerPersonalizedProductDetails } from 'api/product/getPersonalizedProductDetails';
import { PersonalDiscountedVariant } from 'api/product/getPersonalizedProductDetails/types';
import { Entry } from 'contentful';
import isArray from 'lodash/isArray';
import { ContentfulProvider } from 'modules/content-cms/components/context';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { axios } from 'utils/axios';
import { configureAxiosInstance } from 'utils/configure-axios';
import getShopId from 'utils/i18n/get-shop-id';
import { fetchProductDetails } from '../../api/product';
import {
  FetchContentRes,
  fetchContentfulProductPage,
  fetchContentfulShopifyData,
} from '../../connectors/contentful';
import { SubscriptionInterval } from '../../constants/order';
import { IProductPageFields, IProductPagesFields } from '../../global';
import ProductDetails from '../../modules/product-details';
import { ProductDetailsPage } from '../../modules/product-details/types';
import withFallback, { StatusCode } from '../../utils/with-fallback';

interface PropTypes {
  productDetails: ProductDetailsPage['productDetails'];
  contentfulData: Entry<IProductPageFields | IProductPagesFields>;
  contentfulHandle: string;
  defaultPriceVariants: PersonalDiscountedVariant[];
  contentfulShopifyData: FetchContentRes;
}

const ProductPage: NextPage<PropTypes> = ({
  contentfulHandle,
  contentfulData,
  productDetails,
  defaultPriceVariants,
  contentfulShopifyData,
}) => {
  const { query } = useRouter();
  const { variant, interval } = query;

  const variantId = isArray(variant) ? variant[0] : variant;

  return (
    <ContentfulProvider
      shopifyData={contentfulShopifyData}
      handle={contentfulHandle}
      isProductPage={true}
      fallbackData={contentfulData}
    >
      <ProductDetails
        productDetails={productDetails}
        initialVariantId={variantId ? variantId : undefined}
        interval={interval as SubscriptionInterval}
        defaultPriceVariants={defaultPriceVariants}
      />
    </ContentfulProvider>
  );
};
interface UrlQuery extends ParsedUrlQuery {
  handle: string;
}

export const getStaticProps: GetStaticProps<
  PropTypes | StatusCode,
  UrlQuery
> = async (context) => {
  const locale = context.locale;
  const shopId = getShopId(locale);
  const handle = context.params?.handle;
  const contentfulHandle = `products/${handle}`;

  const cleanup = configureAxiosInstance(axios, locale);

  if (!handle) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  try {
    const productDetails = await fetchProductDetails(handle, shopId).catch(
      (error) => {
        console.error('[Product Page] Product details fetch error:', error);
        return null;
      }
    );

    if (!productDetails) {
      return {
        notFound: true,
        revalidate: true,
      };
    }

    const contentfulData = await fetchContentfulProductPage({
      handle: contentfulHandle,
      locale,
    });

    if (!contentfulData) {
      console.error('[Product Page] Failed to fetch Contentful data');
      return { notFound: true, revalidate: true };
    }

    const [{ variants }, contentfulShopifyData] = await Promise.all([
      gerPersonalizedProductDetails({ product_id: productDetails.product.id }),
      fetchContentfulShopifyData(contentfulData, shopId),
    ]);

    return {
      props: {
        contentfulData,
        contentfulHandle,
        contentfulShopifyData,
        productDetails,
        defaultPriceVariants: variants,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error) {
    console.error('[Product Page] Critical error:', {
      handle,
      locale,
      error: error instanceof Error ? error.message : error,
    });

    return {
      notFound: true,
      revalidate: true,
    };
  } finally {
    cleanup();
  }
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default withFallback(ProductPage);
